/**
 * PDF Template Service
 * Manages PDF templates with API integration
 * Handles CRUD operations and file upload workflows
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { PDF_TEMPLATE_ENDPOINTS, buildQueryString } from '../endpoints';

/**
 * @typedef {Object} CreatePdfTemplateDto
 * @property {string} name - Template name (1-200 chars)
 * @property {string} [description] - Description (max 1000 chars)
 * @property {number} formSchemaId - Associated form ID
 * @property {string} pdfmeSchemaJson - pdfme schema as JSON string
 * @property {string} tempBasePdfPath - Temp blob path from FileUpload
 * @property {Array<{key: string, tempPath: string, description?: string}>} [tempAssets] - Static assets
 * @property {boolean} [isDefault] - Mark as default
 * @property {string} [paperSize] - Paper size (max 50 chars)
 * @property {string} [orientation] - Orientation (max 20 chars)
 */

/**
 * @typedef {Object} UpdatePdfTemplateDto
 * All fields from CreatePdfTemplateDto but optional
 */

/**
 * @typedef {Object} PdfTemplateResponseDto
 * @property {number} id - Template ID
 * @property {string} name - Template name
 * @property {string} [description] - Description
 * @property {number} formSchemaId - Form schema ID
 * @property {string} pdfmeSchemaJson - pdfme schema JSON
 * @property {string} basePdfUrl - URL to base PDF (with SAS token)
 * @property {Array<{key: string, url: string, description?: string}>} assets - Asset URLs
 * @property {boolean} isDefault - Is default template
 * @property {boolean} isActive - Is active
 * @property {string} [paperSize] - Paper size
 * @property {string} [orientation] - Orientation
 * @property {string} createdByName - Creator name
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 */

/**
 * Validate CreatePdfTemplateDto
 */
const validateCreateDto = (dto) => {
  const errors = [];

  if (!dto.name || dto.name.trim().length === 0) {
    errors.push('Template name is required');
  }
  if (dto.name && dto.name.length > 200) {
    errors.push('Template name must not exceed 200 characters');
  }

  if (dto.description && dto.description.length > 1000) {
    errors.push('Description must not exceed 1000 characters');
  }

  if (!dto.formSchemaId) {
    errors.push('Form schema ID is required');
  }

  if (!dto.pdfmeSchemaJson || dto.pdfmeSchemaJson.trim().length === 0) {
    errors.push('PDF schema is required');
  }

  if (dto.tempBasePdfPath && dto.tempBasePdfPath.length > 500) {
    errors.push('Base PDF path must not exceed 500 characters');
  }

  if (dto.paperSize && dto.paperSize.length > 50) {
    errors.push('Paper size must not exceed 50 characters');
  }

  if (dto.orientation && dto.orientation.length > 20) {
    errors.push('Orientation must not exceed 20 characters');
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validate UpdatePdfTemplateDto (all fields optional)
 */
const validateUpdateDto = (dto) => {
  const errors = [];

  if (dto.name !== undefined && dto.name.length > 200) {
    errors.push('Template name must not exceed 200 characters');
  }

  if (dto.description !== undefined && dto.description.length > 1000) {
    errors.push('Description must not exceed 1000 characters');
  }

  if (dto.tempBasePdfPath !== undefined && dto.tempBasePdfPath.length > 500) {
    errors.push('Base PDF path must not exceed 500 characters');
  }

  if (dto.paperSize !== undefined && dto.paperSize.length > 50) {
    errors.push('Paper size must not exceed 50 characters');
  }

  if (dto.orientation !== undefined && dto.orientation.length > 20) {
    errors.push('Orientation must not exceed 20 characters');
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Blank A4 PDF template definition for fallback
 */
const BLANK_A4_TEMPLATE = {
  width: 210,    // A4 width in mm
  height: 297,   // A4 height in mm
  padding: [10, 10, 10, 10]
};

const pdfTemplateService = {
  /**
   * Blank A4 template definition
   */
  BLANK_A4_TEMPLATE,

  /**
   * Get all templates for a form
   * @param {number} formSchemaId - Form schema ID
   * @param {boolean} [includeInactive=false] - Include inactive templates
   * @returns {Promise<Array<PdfTemplateResponseDto>>}
   */
  getTemplatesByFormId: async (formSchemaId, includeInactive = false) => {
    const queryString = buildQueryString({ includeInactive });
    const response = await apiRequest(() =>
      axiosInstance.get(`${PDF_TEMPLATE_ENDPOINTS.LIST_BY_FORM(formSchemaId)}${queryString}`)
    );

    // Handle .NET $values array format
    return response.$values || response || [];
  },

  /**
   * Get single template by ID
   * @param {number} id - Template ID
   * @returns {Promise<PdfTemplateResponseDto>}
   */
  getTemplateById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(PDF_TEMPLATE_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Get default template for a form
   * @param {number} formSchemaId - Form schema ID
   * @returns {Promise<PdfTemplateResponseDto|null>}
   */
  getDefaultTemplate: async (formSchemaId) => {
    try {
      return await apiRequest(() =>
        axiosInstance.get(PDF_TEMPLATE_ENDPOINTS.GET_DEFAULT(formSchemaId))
      );
    } catch (error) {
      // Return null if no default template exists (404)
      if (error.status === 404) return null;
      throw error;
    }
  },

  /**
   * Create new template
   * @param {CreatePdfTemplateDto} templateData - Template data
   * @returns {Promise<PdfTemplateResponseDto>}
   */
  createTemplate: async (templateData) => {
    // Validate before sending
    const validation = validateCreateDto(templateData);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    return apiRequest(() =>
      axiosInstance.post(PDF_TEMPLATE_ENDPOINTS.CREATE, templateData)
    );
  },

  /**
   * Update existing template
   * @param {number} id - Template ID
   * @param {UpdatePdfTemplateDto} templateData - Update data
   * @returns {Promise<PdfTemplateResponseDto>}
   */
  updateTemplate: async (id, templateData) => {
    // Validate if fields are provided
    if (Object.keys(templateData).length > 0) {
      const validation = validateUpdateDto(templateData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }
    }

    return apiRequest(() =>
      axiosInstance.put(PDF_TEMPLATE_ENDPOINTS.UPDATE(id), templateData)
    );
  },

  /**
   * Delete template (soft delete)
   * @param {number} id - Template ID
   * @returns {Promise<void>}
   */
  deleteTemplate: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(PDF_TEMPLATE_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Permanently delete template
   * @param {number} id - Template ID
   * @returns {Promise<void>}
   */
  deleteTemplatePermanently: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(PDF_TEMPLATE_ENDPOINTS.DELETE_PERMANENT(id))
    );
  },

  /**
   * Set template as default for form
   * @param {number} id - Template ID
   * @returns {Promise<PdfTemplateResponseDto>}
   */
  setAsDefault: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(PDF_TEMPLATE_ENDPOINTS.SET_DEFAULT(id))
    );
  },

  /**
   * Duplicate template with new name
   * @param {number} id - Template ID to duplicate
   * @param {string} newName - Name for duplicated template
   * @returns {Promise<PdfTemplateResponseDto>}
   */
  duplicateTemplate: async (id, newName) => {
    if (!newName || newName.trim().length === 0) {
      throw new Error('New name is required for duplicate template');
    }
    if (newName.length > 200) {
      throw new Error('Template name must not exceed 200 characters');
    }

    return apiRequest(() =>
      axiosInstance.post(
        PDF_TEMPLATE_ENDPOINTS.DUPLICATE(id),
        JSON.stringify(newName),
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
    );
  },

  /**
   * Parse API response back to pdfme template
   * @param {string} pdfmeSchemaJson - JSON from API
   * @param {string} [basePdf] - Base PDF URL or definition
   * @returns {Object} pdfme template object
   */
  deserializePdfmeTemplate: (pdfmeSchemaJson, basePdf) => {
    const finalBasePdf = basePdf || BLANK_A4_TEMPLATE;

    if (!pdfmeSchemaJson) {
      return {
        basePdf: finalBasePdf,
        schemas: [[]]
      };
    }

    try {
      const parsed = JSON.parse(pdfmeSchemaJson);

      // Extract schemas - handle both wrapped object and direct array format
      let schemas = parsed.schemas || (Array.isArray(parsed) ? parsed : [[]]);

      return {
        basePdf: finalBasePdf,
        ... (parsed.schemas ? parsed : { schemas })
      };
    } catch (e) {
      console.error('Failed to parse pdfmeSchemaJson:', e);
      return {
        basePdf: finalBasePdf,
        schemas: [[]]
      };
    }
  },

  /**
   * Convert pdfme template object to API format
   * @param {Object} pdfmeTemplate - pdfme template object
   * @returns {string} JSON string for API
   */
  serializePdfmeTemplate: (pdfmeTemplate) => {
    // Remove basePdf from schema - it's stored separately via tempBasePdfPath
    const { basePdf, ...schemaWithoutBasePdf } = pdfmeTemplate;
    return JSON.stringify(schemaWithoutBasePdf);
  },

  /**
   * Parse image report config JSON from API response
   * @param {string} json - Image report config JSON string
   * @returns {Object|null} Parsed config or null
   */
  parseImageReportConfig: (json) => {
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch (e) {
      console.error('Failed to parse imageReportConfigJson:', e);
      return null;
    }
  },

  /**
   * Serialize image report config to JSON string
   * @param {Object} config - Image report config object
   * @returns {string} JSON string
   */
  serializeImageReportConfig: (config) => {
    if (!config) return '';
    return JSON.stringify(config);
  },

  /**
   * Parse API response back to pdfme template
   * @param {Object} pdfmeTemplate - pdfme template
   * @returns {{isValid: boolean, errors: Array<string>}}
   */
  validateTemplate: (pdfmeTemplate) => {
    const errors = [];

    if (!pdfmeTemplate) {
      errors.push('Template is required');
      return { isValid: false, errors };
    }

    if (!pdfmeTemplate.schemas) {
      errors.push('Template must have schemas property');
    }

    if (pdfmeTemplate.schemas && !Array.isArray(pdfmeTemplate.schemas)) {
      errors.push('Template schemas must be an array');
    }

    if (pdfmeTemplate.schemas && Array.isArray(pdfmeTemplate.schemas)) {
      if (pdfmeTemplate.schemas.length === 0) {
        errors.push('Template must have at least one schema');
      }

      pdfmeTemplate.schemas.forEach((schema, index) => {
        if (!Array.isArray(schema)) {
          errors.push(`Schema at index ${index} must be an array`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },
};

export default pdfTemplateService;
